import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, ArrowDownRight, ChevronRight } from "lucide-react";
import { Header } from "@/components/Header";
import { Sparkline } from "@/components/Sparkline";
import { usePrices, fmtPrice } from "@/hooks/usePrices";
import { Skeleton } from "@/components/ui/skeleton";

function CoinRow({ c, i, onOpen }) {
  const up = (c.change24h || 0) >= 0;
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: i * 0.04 }}
      data-testid={`crypto-price-${c.symbol.toLowerCase()}`}
      onClick={() => onOpen(c.symbol)}
      className="flex items-center gap-3 px-4 py-3.5 border-b border-[#1E293B]/60 cursor-pointer active:bg-[#1E2330]/40 transition-colors"
    >
      <div className="w-9 h-9 rounded-full bg-[#1E2330] flex items-center justify-center overflow-hidden shrink-0">
        {c.image ? (
          <img src={c.image} alt={c.symbol} className="w-full h-full object-cover" />
        ) : (
          <span className="text-[11px] font-bold text-slate-300">{c.symbol.slice(0, 3)}</span>
        )}
      </div>
      <div className="min-w-0">
        <div className="font-semibold text-sm leading-tight">{c.symbol}</div>
        <div className="text-xs text-slate-500 truncate">{c.name}</div>
      </div>
      <div className="ml-auto flex items-center gap-3">
        <Sparkline data={c.sparkline} up={up} width={72} height={30} />
        <div className="text-right w-[92px]">
          <div className="font-mono-num text-sm font-semibold">${fmtPrice(c.price)}</div>
          <div
            className={`font-mono-num text-xs flex items-center justify-end gap-0.5 ${
              up ? "text-[#00E676]" : "text-[#FF3B30]"
            }`}
          >
            {up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
            {Math.abs(c.change24h || 0).toFixed(2)}%
          </div>
        </div>
        <ChevronRight size={16} className="text-slate-600 shrink-0" />
      </div>
    </motion.div>
  );
}

export default function Markets() {
  const { coins, loading } = usePrices();
  const navigate = useNavigate();
  const open = (symbol) => navigate(`/coin/${symbol}`);
  const gainers = [...coins].sort((a, b) => (b.change24h || 0) - (a.change24h || 0)).slice(0, 4);

  return (
    <div className="pb-24 grain">
      <Header title="Markets" />

      <div className="max-w-md mx-auto">
        {/* hero */}
        <div className="px-4 pt-5 pb-3">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative rounded-2xl overflow-hidden border border-[#1E293B] p-5 bg-[#121721]"
          >
            <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-[#00E676]/10 blur-2xl" />
            <h1 className="font-display text-2xl font-bold tracking-tight leading-tight">
              Choose your direction.<br />
              <span className="text-[#00E676]">Pull up</span> or{" "}
              <span className="text-[#FF3B30]">Call down</span>.
            </h1>
            <p className="text-xs text-slate-400 mt-2">Tier-based wins · Loss rate is half the win rate · 1 entry/day</p>
          </motion.div>
        </div>

        {/* top movers */}
        {!loading && gainers.length > 0 && (
          <div className="px-4 pb-2">
            <div className="grid grid-cols-2 gap-3">
              {gainers.map((c) => {
                const up = (c.change24h || 0) >= 0;
                return (
                  <div
                    key={c.id}
                    onClick={() => open(c.symbol)}
                    className="rounded-xl border border-[#1E293B] bg-[#121721] p-3 cursor-pointer active:bg-[#1E2330]/60 transition-colors"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {c.image && <img src={c.image} alt="" className="w-5 h-5 rounded-full" />}
                      <span className="text-xs font-semibold">{c.symbol}</span>
                    </div>
                    <div className="font-mono-num text-sm font-semibold">${fmtPrice(c.price)}</div>
                    <div className={`font-mono-num text-xs ${up ? "text-[#00E676]" : "text-[#FF3B30]"}`}>
                      {up ? "+" : ""}
                      {(c.change24h || 0).toFixed(2)}%
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="px-4 pt-4 pb-1 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-300">All Markets</h2>
          <span className="text-[10px] text-slate-500 font-mono-num">USD</span>
        </div>

        <div className="rounded-2xl overflow-hidden border border-[#1E293B] mx-4 bg-[#121721]">
          {loading
            ? Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3.5 border-b border-[#1E293B]/60">
                  <Skeleton className="w-9 h-9 rounded-full bg-[#1E2330]" />
                  <Skeleton className="h-4 w-16 bg-[#1E2330]" />
                  <Skeleton className="h-6 w-20 ml-auto bg-[#1E2330]" />
                </div>
              ))
            : coins.map((c, i) => <CoinRow key={c.id} c={c} i={i} onOpen={open} />)}
        </div>
      </div>
    </div>
  );
}
